// @ts-ignore - api/ is copied in from comprehendpt_front_end/src by cx_embed/build.py
import { noteMessage, notePageContext } from '../api/replay';
// @ts-ignore - agent/ is copied in from comprehendpt_front_end/src by cx_embed/build.py
import { railState, saveLayoutFromRail, setRailMirror } from '../utils/workspace/workspaceLayout';
import { multiRecorderBusy } from '../agent/multiRecorder';

var nScraped = 0;
const STALE_CONTEXT_MESSAGE = '__STALE_CONTEXT__';

let _domReqSeq = 0;
const _pendingDomReq: Map<
  string,
  {
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
    timeoutId: any;
  }
> = new Map();

function ensureDomBridgeListener() {
  if (typeof window === 'undefined') return;
  if ((window as any).__COMP_DOM_BRIDGE_LISTENER__) return;
  (window as any).__COMP_DOM_BRIDGE_LISTENER__ = true;

  window.addEventListener('message', (ev: MessageEvent) => {
    const data: any = (ev as any).data;
    if (!data || !data.__comp) return;
    if (data.type !== 'DOM_RESPONSE' || !data.id) return;
    const pending = _pendingDomReq.get(String(data.id));
    if (!pending) return;
    _pendingDomReq.delete(String(data.id));
    clearTimeout(pending.timeoutId);
    if (data.error) pending.reject(new Error(String(data.error)));
    else pending.resolve(data.result);
  });
}

async function domRequest(payload: any, timeoutMs = 5000): Promise<any> {
  ensureDomBridgeListener();

  if (typeof window === 'undefined') {
    throw new Error('NO_WINDOW');
  }
  if (window === window.parent) {
    // Not embedded, nothing to talk to.
    throw new Error('NO_PARENT');
  }

  const id = `${Date.now()}_${++_domReqSeq}`;
  // Mirror the panel <-> EMR-page protocol onto any running replay. Wrapped so a
  // logging failure can never affect the request. Recorded unmasked — see
  // api/replay/sidecar.js.
  const logMessage = (response: any, error?: any) => {
    try {
      const type = payload && (payload.type || payload.fn || payload.action);
      // Not the replay's own plumbing: GET_EMR_REPLAY_EVENTS IS the EMR DOM
      // drain, so mirroring its response would store every batch of EMR events
      // a second time. Kept in step with the extension's copy of this file.
      if (type !== 'GET_EMR_REPLAY_EVENTS') {
        noteMessage({
          channel: 'postMessage',
          dir: 'panel→page',
          type,
          args: payload,
          response,
          error: error ? String(error && error.message ? error.message : error) : undefined,
        });
      }
      // Page identity for the research view (which EMR screens people use).
      const result = response && (response.result || response);
      if (type === 'PAGE_INFO_REQUEST' && result && result['URL']) {
        notePageContext({
          url: result['URL'],
          noteType: result['Note Type'],
          surface: 'cx',
        });
      }
    } catch (e) {
      /* never surface */
    }
  };

  return await new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      _pendingDomReq.delete(id);
      logMessage(null, 'DOM_REQUEST_TIMEOUT');
      reject(new Error('DOM_REQUEST_TIMEOUT'));
    }, timeoutMs);

    _pendingDomReq.set(id, {
      resolve: (value: any) => {
        logMessage(value);
        resolve(value);
      },
      reject: (reason?: any) => {
        logMessage(null, reason);
        reject(reason);
      },
      timeoutId,
    });
    window.parent.postMessage({ __comp: true, type: 'DOM_REQUEST', id, payload }, '*');
  });
}

/**
 * Drain the EMR page's rrweb buffer for the session replay.
 *
 * Routed through the same parent-page DOM bridge as everything else here. If the
 * host page's bridge does not implement it, this rejects and the pump gives up
 * after a few tries — the replay simply has no EMR side, which is the same
 * outcome as before this existed.
 */
export async function getEmrReplayEvents({ checkout = false } = {}) {
  // Same argument shape as the extension's copy; the bridge hands `payload` to
  // the evaluator as the message, so `args` lands as msg.args there too.
  const response = await domRequest({ type: 'GET_EMR_REPLAY_EVENTS', args: { checkout } });
  return response?.result || response;
}

export async function getPageInfo() {
  // Default for plain web (no extension / no embed bridge)
  const fallback = {
    URL: 'app.comprehendpt.com',
    'Note Type': 'Unsupported in App',
  };

  if (!externalConnectionAvailable()) return fallback;

  try {
    const res = await domRequest({ type: 'PAGE_INFO_REQUEST', args: {} }, 5000);
    return res?.result || res || fallback;
  } catch {
    return fallback;
  }
}

export async function getPageMetadata() {
  // The EMR's own note metadata. CORA Sapphire publishes a noteMetadata JSON
  // blob whose isMC / isWC / isPeds flags decide which compliance rule set
  // grades the note. Returns null when the EMR publishes nothing, which is the
  // normal case outside CORA.
  if (!externalConnectionAvailable()) return null;

  try {
    const res = await domRequest({ type: 'PAGE_METADATA_REQUEST', args: {} }, 5000);
    const metadata = (res?.result ?? res)?.['Metadata'] ?? null;
    return metadata && typeof metadata === 'object' ? metadata : null;
  } catch {
    return null;
  }
}

export async function getPatientInfo() {
  const fallback = {
    'EMR ID': '',
    'First Name': '',
    'Last Name': '',
    DOB: '',
    Metadata: '',
  };

  if (!externalConnectionAvailable()) return fallback;

  try {
    const res = await domRequest({ type: 'PATIENT_INFO_REQUEST', args: {} }, 5000);
    return res?.result || res || fallback;
  } catch {
    return fallback;
  }
}

export async function getScheduleFromEMR() {
  // WebPT schedule sync relies on the Chrome extension background script.
  // The embedded solution cannot support it, so avoid posting SCHEDULE_REQUEST
  // into the DOM bridge where it would time out.
  return { appointments: [] };
}

export async function getQuestionsFromEMR(
  isWebPTUser = false,
  dropdownCache: any = {},
  replyType: any = {},
  empowerNewVersion = false,
  newRaintreeIntegration = false,
  newPromptIntegration = false,
) {
  if (!externalConnectionAvailable()) return { questions: [], dropdownCache: {}, dropdownMisses: {} };

  // Match the extension message format: GET_DOM uses `msg` (not `args`)
  const res = await domRequest(
    {
      type: 'GET_DOM',
      msg: { isWebPTUser, dropdownCache, replyType, empowerNewVersion, newRaintreeIntegration, newPromptIntegration },
    },
    120000,
  ).catch(() => null);

  if (!res) return { questions: [], dropdownCache: {}, dropdownMisses: {} };
  if (res.questions) {
    return {
      questions: res.questions,
      dropdownCache: res.dropdownCache || {},
      dropdownMisses: res.dropdownMisses || {},
    };
  }
  // Some injectors may return `{ result: { questions: ... } }`
  const inner = res.result || {};
  return {
    questions: inner.questions || [],
    dropdownCache: inner.dropdownCache || {},
    dropdownMisses: inner.dropdownMisses || {},
  };
}

export function externalConnectionAvailable() {
  // This function is called to check if the EMR is available
  // It returns true in the CX, Native App, and on iOS...
  try {
    return typeof window !== 'undefined' && window !== window.parent;
  } catch {
    return false;
  }
}

export function fillActiveField(rsp) {
  // This on iOS should send the bluetooth message...
  // In embed mode, ask the page-context injector to fill the active field.
  return domRequest({ type: 'FILL_ACTIVE_FIELD', args: { value: rsp } }, 8000);
}

export function answerQuestionInEMR(
  question: string,
  answer: string,
  replyType: any,
  expectedNoteKey?: string,
) {
  if (!externalConnectionAvailable()) return false;
  return domRequest(
    {
      type: 'PUSH_ANSWER',
      args: {
        Question: question,
        Answer: answer,
        'Reply Type': replyType,
        __expectedNoteKey: expectedNoteKey,
      },
    },
    120000,
  ).then((res) => res?.result ?? res);
}

export function answerQuestionsInEMR(answers: any[]) {
  if (!Array.isArray(answers) || answers.length === 0) return;
  for (let i = 0; i < answers.length; i++) {
    try {
      answerQuestionInEMR(
        answers[i]?.Question,
        answers[i]?.Answer,
        answers[i]?.['Reply Type'],
      );
    } catch (_) {}
  }
}

export function getHaveInjector() {
  // In embed mode (iframe), ask the page-context injector via loader.js bridge.
  return domRequest({ type: 'GET_HAVE_INJECTOR', args: {} }, 4000)
    .then((res) => {
      if (res === true) return true;
      return !!(res && (res.result === true || res.haveInjector === true));
    })
    .catch(() => false);
}

export function startScreenCapture() {
  // This function is called to start the screen capture
  // It is not supported in the App
  return domRequest({ type: 'START_SCREEN_CAPTURE', args: {} }, 8000);
}

export function getSessionData() {
  // It is not supported in the App
  return domRequest({ type: 'GET_SESSION_DATA', args: {} }, 8000);
}

export function getPageUrl() {
  // Return mock URL for the front end app
  return domRequest({ type: 'GET_PAGE_URL', args: {} }, 4000).catch(() => ({
    url: 'app.comprehendpt.com',
  }));
}


export function highlightFields() {
  if (!externalConnectionAvailable()) return false;
  return domRequest({ type: 'HIGHLIGHT_FIELDS', args: {} }, 8000).then(
    (res) => res?.result ?? res,
  );
}

function computeFingerprint(patientInfo: any, pageInfo: any): string {
  return [
    patientInfo?.['EMR ID'] || '',
    patientInfo?.['DOB'] || '',
    pageInfo?.['URL'] || '',
    pageInfo?.['Note Type'] || '',
  ].join('|');
}

export async function createEMRSession() {
  const [pageInfo, patientInfo] = await Promise.all([
    getPageInfo(),
    getPatientInfo(),
  ]);
  const fingerprint = computeFingerprint(patientInfo, pageInfo);
  let disposed = false;
  const generatedAnswers: Record<string, any> = {};
  const readNoteIdentity = async () => {
    try {
      const response = await domRequest(
        {
          type: 'GET_NOTE_IDENTITY',
          args: { expectedFingerprint: fingerprint },
        },
        1000,
      );
      if (
        response &&
        Object.prototype.hasOwnProperty.call(response, 'result')
      ) {
        return response.result;
      }
      return response;
    } catch (_) {
      return undefined;
    }
  };

  const noteIdentity = externalConnectionAvailable()
    ? await readNoteIdentity()
    : undefined;
  const expectedNoteKey = noteIdentity?.noteKey;
  // Embed host and evaluator files can briefly come from different releases.
  const legacyPageUrl = noteIdentity?.pageUrl || '';
  const noteCaptureSupported = Boolean(expectedNoteKey);

  return {
    tabId: -1,
    fingerprint,
    initialPageInfo: pageInfo,
    initialPatientInfo: patientInfo,
    isDisposed: () => disposed,
    dispose: () => {
      disposed = true;
    },
    getPageInfo: async () => (disposed ? null : getPageInfo()),
    getPageMetadata: async () => (disposed ? null : getPageMetadata()),
    getPatientInfo: async () => (disposed ? null : getPatientInfo()),
    getQuestionsFromEMR: async (
      isWebPTUser = false,
      dropdownCache: any = {},
      replyType: any = {},
      empowerNewVersion = false,
      newRaintreeIntegration = false,
      newPromptIntegration = false,
    ) =>
      disposed
        ? null
        : getQuestionsFromEMR(
            isWebPTUser,
            dropdownCache,
            replyType,
            empowerNewVersion,
            newRaintreeIntegration,
            newPromptIntegration,
          ),
    answerQuestionInEMR: async (
      question: string,
      answer: any,
      replyType: any,
      questionKey?: string,
    ) => {
      if (disposed) return null;
      const result = await answerQuestionInEMR(
        question,
        answer,
        replyType,
        expectedNoteKey,
      );
      if (result === STALE_CONTEXT_MESSAGE) {
        disposed = true;
        return null;
      }
      if (questionKey && answer !== null && answer !== undefined) {
        generatedAnswers[questionKey] = answer;
      }
      return result;
    },
    linkNoteToConversation: async (
      conversationId: number,
      configId: number,
    ) => {
      if (
        disposed ||
        !noteCaptureSupported ||
        !externalConnectionAvailable()
      ) {
        return false;
      }

      return domRequest(
        {
          type: 'LINK_NOTE_TO_CONVERSATION',
          args: {
            conversationId,
            configId,
            generatedAnswers,
            expectedNoteKey,
            expectedFingerprint: fingerprint,
            expectedPageUrl: legacyPageUrl,
          },
        },
        30000,
      )
        .then((result) => Boolean(result?.result ?? result))
        .catch(() => false);
    },
  };
}

export function getCXVersion() {
  return -1
}

export function injectComplianceBadge(grade: string | null, reasons: any[]) {
  if (!externalConnectionAvailable()) return Promise.resolve();
  return domRequest(
    { type: 'INJECT_COMPLIANCE_BADGE', args: { grade, reasons } },
    3000,
  ).catch(() => {});
}

export function removeComplianceBadge() {
  if (!externalConnectionAvailable()) return Promise.resolve();
  return domRequest({ type: 'REMOVE_COMPLIANCE_BADGE', args: {} }, 3000).catch(() => {});
}

/** Tell the compliance rail on the EMR page (loader.js) about a grade this panel produced. */
export function reportComplianceScoreToRail(
  conversationID: number,
  score: number,
  requirementEvaluations: any[],
) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          __comp: true,
          type: 'FREE_COMPLIANCE_MIRROR_PANEL_SCORE',
          conversationID,
          score,
          requirement_evaluations: requirementEvaluations,
        },
        '*',
      );
    }
  } catch (_) {
    /* nothing to do -- the rail just keeps its previous grade */
  }
  return Promise.resolve();
}

/* -------------------------------------------------------------------------- */
/* Compliance rail                                                            */
/*                                                                            */
/* The rail on the EMR page (ComplianceRail.ts, mounted by loader.js) has no   */
/* background worker here, so this panel answers it: the session lives here.  */
/* The requests and API calls are the ones the extension's background.js      */
/* handles (runFreeComplianceScore and friends) -- keep the two in step.       */
/* -------------------------------------------------------------------------- */

/** The rail's requests: background.js's FREE_COMPLIANCE_* plus the embed's reload check. */
const RAIL_REQUEST_TYPES = new Set([
  'FREE_COMPLIANCE_CHECK_SESSION',
  'FREE_COMPLIANCE_SCORE',
  'FREE_COMPLIANCE_RESOLVE_VISIT',
  'FREE_COMPLIANCE_CAN_RELOAD',
  // The tab layout, app catalog and open tab, which the extension's rail reads
  // from chrome.storage and this rail can only get from here.
  'WORKSPACE_STATE',
  'WORKSPACE_LAYOUT_SET',
]);

const RAIL_SIGN_IN = {
  error: 'Sign in to Comprehend in the panel to use this.',
  signInRequired: true,
};

/** An API call that answers an error instead of throwing, as background.js's does. */
async function railApiPost(apiClient: any, path: string, body: any): Promise<any> {
  try {
    return await apiClient.JMPost(path, body);
  } catch (_) {
    return { error: 'Comprehend is not reachable right now. Try again.' };
  }
}

/** EMRs hand us M/D/YYYY; the patient API wants YYYY-MM-DD. */
function normalizeRailDob(dob: any): string {
  if (!dob) return '';
  try {
    const parsed = new Date(dob);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
  } catch (_) {
    return '';
  }
}

/** Whether a visit exists, wasn't deleted (Inactive), and is this page's patient's. */
async function railVisitFits(apiClient: any, conversationId: any, patientId: any): Promise<boolean> {
  const visit = await railApiPost(apiClient, '/v1/conversation/details', { conversationId });
  if (!visit || visit.error) return false;
  if (visit.status === 'Inactive') return false;
  if (patientId && Number(visit.group) !== Number(patientId)) return false;
  return true;
}

/**
 * Which visit this note belongs to, and which template grades it: the rail's
 * own visit, else today's visit for this patient or a new compliance shell
 * (both server-side, in /v1/conversation/new with purpose=compliance).
 */
async function resolveRailConversation(apiClient: any, payload: any): Promise<any> {
  const pageInfo = payload?.pageInfo || {};

  let configId = payload?.configId ?? null;
  const invalidSections = new Set(['none', 'null', 'undefined', '"', '']);
  const sections = (payload?.sections || []).filter(
    (section: any) => !invalidSections.has(String(section).trim().toLowerCase()),
  );

  const template = await railApiPost(apiClient, '/v1/template/emrQuery', {
    emrURL: pageInfo['URL'],
    noteType: pageInfo['Note Type'],
    sections,
  });
  if (template?.configId) {
    configId = Number(template.configId);
  }
  if (!configId) {
    return {
      error: template?.error || 'No Comprehend template matches this note type yet.',
    };
  }

  // The patient on this EMR page (best effort): what a held visit is checked
  // against, and what lets the backend reuse today's visit.
  let patientId = null;
  const patient = payload?.patientInfo;
  if (patient?.['EMR ID']) {
    const found = await railApiPost(apiClient, '/v1/patient/emrQuery', {
      firstName: patient['First Name'] || '',
      lastName: patient['Last Name'] || '',
      dob: normalizeRailDob(patient['DOB']),
      emrID: patient['EMR ID'],
      metadata: patient['Metadata'] || '',
      purpose: 'compliance',
    });
    if (found?.uid) patientId = found.uid;
  }

  // The visit the rail already holds, only if it is still this patient's and
  // wasn't deleted (Inactive); otherwise today's visit for the patient (the
  // backend skips deleted ones) or a new compliance shell.
  let conversationId = null;
  if (payload?.conversationId && (await railVisitFits(apiClient, payload.conversationId, patientId))) {
    conversationId = payload.conversationId;
  }
  if (!conversationId) {
    const created = await railApiPost(apiClient, '/v1/conversation/new', {
      configId,
      ...(patientId ? { patientId } : {}),
      purpose: 'compliance',
    });
    if (!created?.uid) {
      return { error: created?.error || 'Could not start a compliance check.' };
    }
    conversationId = created.uid;
  }

  return { conversationId, configId };
}

async function scoreRailNote(apiClient: any, payload: any): Promise<any> {
  const visit = await resolveRailConversation(apiClient, payload);
  if (visit.error) return visit;
  const { conversationId, configId } = visit;

  const scored = await railApiPost(apiClient, '/v1/conversation/score', {
    conversationId,
    existingValues: payload?.existingValues || {},
    ...(payload?.emrMetadata ? { emrMetadata: payload.emrMetadata } : {}),
  });
  if (scored?.error) {
    return { error: scored.error, conversationId, configId };
  }
  const answer = scored?.answer;
  if (!answer) {
    return { error: 'Compliance score unavailable. Try again.', conversationId, configId };
  }
  return {
    conversationId,
    configId,
    score: answer.score,
    requirement_evaluations: answer.requirement_evaluations || answer.feedback || [],
    criteria_source: answer.criteria_source || 'cms',
  };
}

async function answerRailRequest(apiClient: any, message: any): Promise<any> {
  const signedIn = Boolean(apiClient?.haveSessionKey?.() && apiClient?.sessionEmail);
  switch (message?.type) {
    case 'FREE_COMPLIANCE_CHECK_SESSION':
      return signedIn ? { signedIn: true } : { signedIn: false, ...RAIL_SIGN_IN };
    case 'FREE_COMPLIANCE_SCORE':
      return signedIn ? scoreRailNote(apiClient, message.payload) : RAIL_SIGN_IN;
    case 'FREE_COMPLIANCE_RESOLVE_VISIT':
      return signedIn ? resolveRailConversation(apiClient, message.payload) : RAIL_SIGN_IN;
    case 'WORKSPACE_STATE':
      return railState();
    case 'WORKSPACE_LAYOUT_SET':
      if (message.layout) saveLayoutFromRail(message.layout);
      return { saved: true };
    case 'FREE_COMPLIANCE_CAN_RELOAD':
      // The loader reloads this panel to open a visit on a tab. Recorder (and an
      // in-flight edit) set onbeforeunload while leaving would lose work; the
      // Multi Recorder and Comprehend Live recorders say so through multiRecorderBusy.
      return { canReload: !window.onbeforeunload && !multiRecorderBusy() };
    default:
      return { error: `unknown compliance message: ${message?.type}` };
  }
}

/** Answer the rail's requests, which loader.js relays from the EMR page. */
export function setupComplianceRailBridge(apiClient: any): () => void {
  if (typeof window === 'undefined' || window.parent === window) return () => {};
  const reply = (message: any) => {
    try {
      window.parent.postMessage({ __comp: true, ...message }, '*');
    } catch (_) {
      /* the page went away */
    }
  };
  const handler = (ev: MessageEvent) => {
    const data: any = (ev as any).data;
    if (!data || !data.__comp || ev.source !== window.parent) return;
    if (data.type === 'FREE_COMPLIANCE_PING') {
      reply({ type: 'FREE_COMPLIANCE_PONG' });
      return;
    }
    if (!RAIL_REQUEST_TYPES.has(data.type) || !data.id) return;
    // Answered as background.js answers its port: the request's id and a result.
    answerRailRequest(apiClient, data)
      .catch((error: any) => ({ error: String(error?.message ?? error) }))
      .then((result) => reply({ type: data.type, id: data.id, result }));
  };
  window.addEventListener('message', handler);
  // Every layout / catalog / open-tab change here goes to the rail as it
  // happens, the way chrome.storage.onChanged tells the extension's rail.
  const offMirror = setRailMirror((key: string, value: any) =>
    reply({ type: 'WORKSPACE_PUSH', key, value })
  );
  return () => {
    window.removeEventListener('message', handler);
    offMirror();
  };
}

export function setupCompliancePenClickCallback(callback: () => void): () => void {
  const handler = (ev: MessageEvent) => {
    const data = (ev as any).data;
    if (!data || !data.__comp) return;
    if (data.type === 'COMPLIANCE_PEN_CLICKED') {
      callback();
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
